import { Field, FieldType } from '../models/field.model';

/**
 * Field dependency validator for handling field enable/disable logic
 * and dependency chain management
 */
export class FieldDependencyValidator {
  /**
   * Check if a field should be enabled based on its dependencies
   * Handles dependency chains recursively
   * @param field - The field to check
   * @param formValues - Current form values
   * @param allFields - All fields in the form
   * @returns true if field should be enabled, false otherwise
   */
  static isFieldEnabled(field: Field, formValues: {[key: string]: any}, allFields: Field[]): boolean {
    // If field is marked as readOnly, it should always be disabled
    if (field.readOnly) {
      return false;
    }

    // If field has no dependency configuration, it should be enabled
    if (!field.dependsOn) {
      return true;
    }

    // Find the dependency field by key
    const dependencyField = allFields.find(f => f.key === field.dependsOn);
    if (!dependencyField) {
      // Dependency field not found, should clean up dependency configuration
      console.warn(`Dependency field with key ${field.dependsOn} not found for field ${field.key}`);
      return true;
    }

    // Recursively check if dependency field is enabled (dependency chain handling)
    const isDependencyFieldEnabled = this.isFieldEnabled(dependencyField, formValues, allFields);
    if (!isDependencyFieldEnabled) {
      return false; // If dependency field is disabled, current field should also be disabled
    }

    // Get the value of the dependency field using its key
    const dependencyValue = formValues[dependencyField.key];

    // If dependency field has no value yet, disable current field by default
    if (dependencyValue === null || dependencyValue === undefined) {
      return false;
    }

    // Determine if field should be enabled based on dependency configuration
    return dependencyValue === field.enableWhen;
  }

  static isFieldVisible(field: Field, formValues: {[key: string]: any}, allFields: Field[]): boolean {
    if (this.isFieldEnabled(field, formValues, allFields)) {
      return true;
    }
    return field.disabledBehavior !== 'hideAndClear';
  }

  static shouldClearWhenDisabled(field: Field): boolean {
    return field.disabledBehavior === 'hideAndClear';
  }

  /**
   * Clean up dependency configurations when a field is deleted
   * @param deletedFieldKey - Key of the field that was deleted
   * @param allFields - All fields in the form
   * @returns Updated fields array with cleaned dependencies
   */
  static cleanupDependencies(deletedFieldKey: string, allFields: Field[]): Field[] {
    return allFields.map(field => {
      if (field.dependsOn === deletedFieldKey) {
        // Create a copy of the field and remove dependency configuration
        const cleanField = { ...field };
        delete cleanField.dependsOn;
        delete cleanField.enableWhen;
        delete cleanField.disabledBehavior;
        return cleanField;
      }
      return field;
    });
  }

  /**
   * Clear values of disabled fields
   * @param formValues - Current form values
   * @param allFields - All fields in the form
   * @returns Updated form values with disabled field values cleared
   */
  static clearDisabledFieldValues(formValues: {[key: string]: any}, allFields: Field[]): {[key: string]: any} {
    const updatedValues = { ...formValues };

    allFields.forEach(field => {
      if (!this.isFieldEnabled(field, formValues, allFields) && this.shouldClearWhenDisabled(field)) {
        updatedValues[field.key] = null;
      }
    });

    return updatedValues;
  }

  /**
   * Get fields that should be validated (excludes disabled fields)
   * @param allFields - All fields in the form
   * @param formValues - Current form values
   * @returns Array of fields that should participate in validation
   */
  static getValidatableFields(allFields: Field[], formValues: {[key: string]: any}): Field[] {
    return allFields.filter(field =>
      this.isFieldEnabled(field, formValues, allFields) &&
      this.isFieldVisible(field, formValues, allFields)
    );
  }

  /**
   * Check if a field can be used as a dependency (must be Yes/No type)
   * @param field - Field to check
   * @returns true if field can be used as dependency
   */
  static canBeUsedAsDependency(field: Field): boolean {
    return field.type === FieldType.YES_NO;
  }

  /**
   * Get all fields that can be used as dependencies from a list of fields
   * @param allFields - All fields in the form
   * @returns Array of fields that can be used as dependencies
   */
  static getAvailableDependencyFields(allFields: Field[]): Field[] {
    return allFields.filter(field => this.canBeUsedAsDependency(field));
  }

  /**
   * Check for circular dependencies
   * @param fieldKey - Key of the field to check
   * @param dependsOnKey - Key of the field it wants to depend on
   * @param allFields - All fields in the form
   * @returns true if adding this dependency would create a circular dependency
   */
  static hasCircularDependency(fieldKey: string, dependsOnKey: string, allFields: Field[]): boolean {
    const visited = new Set<string>();

    function checkCircular(currentFieldKey: string): boolean {
      if (visited.has(currentFieldKey)) {
        return true; // Circular dependency detected
      }

      if (currentFieldKey === fieldKey) {
        return true; // Would create a cycle back to original field
      }

      visited.add(currentFieldKey);

      const currentField = allFields.find(f => f.key === currentFieldKey);
      if (currentField && currentField.dependsOn) {
        return checkCircular(currentField.dependsOn);
      }

      return false;
    }

    return checkCircular(dependsOnKey);
  }
}
